import { Component, OnDestroy, ViewChild } from '@angular/core';

import { Subject, BehaviorSubject, Subscription, combineLatest, of } from 'rxjs';
import { catchError, filter, map, tap } from 'rxjs/operators';
import * as am4core from '@amcharts/amcharts4/core';
import moment from 'moment-timezone';

import { DateQueryModel } from '../../../../Common/Forms/models/date-query.model';
import { DateQueryTypeEnum } from '../../../../api/mentions/enum/date-query-type.enum';
import { DateTimeChartDataModel } from '../../models/date-time-chart-data.model';
import { PieChartDataModel } from '../../models/pie-chart-data.model';
import { DateTimeChartDataItemModel, DateRange } from '../../models/date-time-chart-data-item.model';
import { ChartExportEventModel } from '../../../../Common/Charts/models/chart-export-event.model';
import { DateFacetTypesEnum } from '../../../../api/mentions/enum/date-facet-types.enum';
import { MentionSearchFacetTypesEnum } from '../../../../api/mentions/enum/mention-search-facet-types.enum';
import { FacetValueModel } from '../../../../api/mentions/model/facet-value.model';
import { MediaTypeEnum, mediaTypeColorKeyMap } from '../../../../Common/Basic/enums/media-type.enum';
import { SearchCriteriaQuerySentimentsEnum } from '../../../../api/mentions/enum/search-criteria-query-sentiments.enum';
import { MentionSearchCriteriaModel } from '../../models/mention-search-criteria.model';
import { monitoringFiltersEnum } from '../../enums/monitoring-filters.enum';
import { CoverageOverTimeComponent } from '../CoverageOverTime/coverage-over-time.component';
import { DonutChartComponent } from '../DonutChart/donut-chart.component';
import { timeScalesEnum } from '../../enums/timeScales.enum';
import { MonitoringRoutingService } from '../../../Core/services/monitoring-routing.service';
import { MonitoringRouteModel } from '../../models/monitoring-route.model';
import { RouteType } from '../../enums/route-type.enum';
import { FacetCollection, MentionSearchService } from '../../../Core/services/mention-search.service';
import { CurrentUserStateService } from '../../../../Core/Users/services/current-user-state.service';
import { messageSeverityEnum } from '../../../../Common/Misc/enums/message-severity.enum';
import { SeriesModel } from '../../../../Common/Charts/models/series.model';
import { DataPointModel } from '../../../../Common/Charts/models/data-point.model';
import { DataListChartComponent } from '../../../../Common/Charts/components/DataListChart/data-list-chart.component';
import { UserPreferencesService } from '../../../../Account/UserPreferences/services/user-preferences.service';
import { userAppPreferencesDefault } from '../../../../Core/Users/data/user-app-preferences-default';
import { UserAppPreferenceChartColors } from '../../../../Core/Users/models/user-app-preferences-chart-colors.model';
import { AbstractChartOptionsModel } from '../../../../Common/Charts/models/abstract-chart-options.model';
import { UserCurrency } from '../../../../Core/Users/enums/user-currency';
import { LocalizationService } from '../../../../Core/Localization/services/localization.service';

/* eslint-disable @typescript-eslint/naming-convention */

@Component({
    selector: 'agpr-monitoring-result-charts',
    templateUrl: './monitoring-result-charts.component.html',
    styleUrls: ['./monitoring-result-charts.component.scss'],
    standalone: false
})
export class MonitoringResultChartsComponent implements OnDestroy {
    static readonly COVERAGE_OVER_TIME_CHART_ID = 'coverageOverTime';
    static readonly SENTIMENT_BREAKDOWN_CHART_ID = 'sentimentBreakdown';
    static readonly MEDIA_TYPE_BREAKDOWN_CHART_ID = 'mediaTypeBreakdown';
    static readonly COVERAGE_SUMMARY_CHART_ID = 'coverageSummary';

    static readonly FILTER_TYPE_SENTIMENT = monitoringFiltersEnum.sentiment;
    static readonly FILTER_TYPE_MEDIA_TYPE = monitoringFiltersEnum.mediaTypes;

    // Order matters, it determines the order of the legend
    private mediaTypeColourMap = new Map<MediaTypeEnum, string>([
        [ MediaTypeEnum.ONLINE_NEWS,   '' ],
        [ MediaTypeEnum.PRINT,         '' ],
        [ MediaTypeEnum.BLOGS,         '' ],
        [ MediaTypeEnum.PRESS_RELEASE, '' ],
        [ MediaTypeEnum.TV,            '' ],
        [ MediaTypeEnum.RADIO,         '' ],
        [ MediaTypeEnum.FACEBOOK,      '' ],
        [ MediaTypeEnum.INSTAGRAM,     '' ],
        [ MediaTypeEnum.REDDIT,        '' ],
        [ MediaTypeEnum.TWITTER,       '' ],
        [ MediaTypeEnum.YOUTUBE,       '' ],
        [ MediaTypeEnum.PODCAST,       '' ],
        [ MediaTypeEnum.TIKTOK,        '' ],
        [ MediaTypeEnum.SNAPCHAT,      '' ],
        [ MediaTypeEnum.THREADS,       '' ],
        [ MediaTypeEnum.LINKEDIN,      '' ],
        [ MediaTypeEnum.BLUESKY,       '' ],
        [ MediaTypeEnum.WECHAT,        '' ],
        [ MediaTypeEnum.SINA_WEIBO,    '' ],
        [ MediaTypeEnum.TWITCH,        '' ],
        [ MediaTypeEnum.PINTEREST,     '' ],
        [ MediaTypeEnum.TUMBLR,        '' ],
        [ MediaTypeEnum.TELEGRAM,      '' ],
        [ MediaTypeEnum.WHATSAPP,      '' ],
        [ MediaTypeEnum.VK,            '' ],
        [ MediaTypeEnum.OTHER,         '' ],
        [ MediaTypeEnum.MAGAZINE,      '' ],
    ]);

    @ViewChild(MonitoringResultChartsComponent.COVERAGE_OVER_TIME_CHART_ID) coverageOverTimeComponent: CoverageOverTimeComponent;
    @ViewChild(MonitoringResultChartsComponent.SENTIMENT_BREAKDOWN_CHART_ID) sentimentBreakdownComponent: DonutChartComponent;
    @ViewChild(MonitoringResultChartsComponent.MEDIA_TYPE_BREAKDOWN_CHART_ID) mediaTypeBreakdownComponent: DonutChartComponent;
    @ViewChild(MonitoringResultChartsComponent.COVERAGE_SUMMARY_CHART_ID) coverageSummaryComponent: DataListChartComponent;

    public classReference = MonitoringResultChartsComponent;

    public coverageOverTimeData: Subject<DateTimeChartDataModel>;
    public mediaTypeBreakdownData: Subject<PieChartDataModel[]>;
    public sentimentBreakdownData: Subject<PieChartDataModel[]>;
    public coverageSummaryData: DataPointModel[];
    public coverageSummaryData$: BehaviorSubject<DataPointModel[]>;
    public coverageSummarySeries: SeriesModel[];
    public searchCriteria: MentionSearchCriteriaModel;
    public currentRoute: MonitoringRouteModel;
    public routeTypeEnum = RouteType;
    public contentNotFound: boolean = false;
    public coverageOverTimeColor: string;
    public coverageSummaryChartName: string = this.classReference.COVERAGE_SUMMARY_CHART_ID;

    public readonly messageSeverityEnum = messageSeverityEnum;

    private subscriptions: Subscription[] = [];
    private facetCollection: FacetCollection;
    private userPreferenceTimezone: string;
    private sentimentColors: UserAppPreferenceChartColors['sentimentColors'];

    chartOptions: AbstractChartOptionsModel = {
        defaultColors: userAppPreferencesDefault.reportsPrefs.defaultChartColors.generalColors,
        currencyCode: UserCurrency.USD
    };

    constructor(
        private mentionSearchService: MentionSearchService,
        private monitoringRoutingService: MonitoringRoutingService,
        private currentUserStateService: CurrentUserStateService,
        private userPreferencesService: UserPreferencesService,
        private localizationService: LocalizationService
    ) {
        this.coverageOverTimeData = new BehaviorSubject<DateTimeChartDataModel>(null);
        this.mediaTypeBreakdownData = new BehaviorSubject<PieChartDataModel[]>(null);
        this.sentimentBreakdownData = new BehaviorSubject<PieChartDataModel[]>(null);

        this.coverageSummaryData$ = new BehaviorSubject<DataPointModel[]>(null);
        this.coverageSummarySeries = [
            {
                'label': null,
                'dataProvider': this.coverageSummaryData$
            }
        ];

        this.subscriptions.push(
            combineLatest([
                this.mentionSearchService.getFilteredFacets().pipe(
                    filter(x => !!x)
                ),
                this.userPreferencesService.getUserAppPreferences().pipe(
                    map(userAppPrefs => userAppPrefs.reportsPrefs.defaultChartColors),
                    catchError(() => of(userAppPreferencesDefault.reportsPrefs.defaultChartColors))
                ),
                this.currentUserStateService.getPreferences().pipe(
                    filter(x => !!x),
                    tap(preference => this.userPreferenceTimezone = preference.timeZone),
                )
            ]).subscribe(([facetCollection, chartColors, preferences]) => {
                Array.from(this.mediaTypeColourMap.keys()).forEach(mediaType => {
                    this.mediaTypeColourMap.set(mediaType, chartColors.mediaTypeColors[mediaTypeColorKeyMap.get(mediaType)]);
                });

                this.sentimentColors = chartColors.sentimentColors;
                this.coverageOverTimeColor = chartColors.generalColors.color1;

                this.facetCollection = facetCollection;
                this.handleDateFacets();
                this.handleFacets();
            }),
            this.mentionSearchService.getCoverageSummary().subscribe(
                coverageSummary => {
                    if (!coverageSummary) {
                        return;
                    }

                    this.coverageSummaryData = coverageSummary;
                    this.coverageSummaryData$.next(this.coverageSummaryData);
                }
            ),
            this.userPreferencesService.getUserAppPreferences().pipe(
                map(userAppPrefs => userAppPrefs.reportsPrefs.defaultChartColors),
                catchError(() => of(userAppPreferencesDefault.reportsPrefs.defaultChartColors))
            ).subscribe(colors => {
                this.chartOptions.defaultColors = colors.generalColors;
            }),
            this.currentUserStateService.getUserCurrency()
                .subscribe(
                    currency => {
                        this.chartOptions.currencyCode = currency;
                    }
                ),
            this.mentionSearchService.getSearchCriteria()
                .subscribe(
                    searchCriteria => {
                        this.searchCriteria = searchCriteria;
                    }
                ),
            this.mentionSearchService.getMentionCount()
                .subscribe(
                    mentionCount => {
                        if (!mentionCount) {
                            return this.contentNotFound = true;
                        }
                        this.contentNotFound = false;
                    }
                ),
            this.monitoringRoutingService.getCurrentRoute()
                .subscribe(
                    route => {
                        if (!route) {
                            return;
                        }
                        this.currentRoute = route;
                    }
                )
        );
    }

    public handleExportClick(event: ChartExportEventModel): void {
        const key = event.eventKey.toLowerCase() as keyof am4core.IExportOptions;
        switch (event.chartId) {
            case MonitoringResultChartsComponent.COVERAGE_OVER_TIME_CHART_ID:
                this.coverageOverTimeComponent.exportData(key);
                break;

            case MonitoringResultChartsComponent.SENTIMENT_BREAKDOWN_CHART_ID:
                this.sentimentBreakdownComponent.exportData(key);
                break;

            case MonitoringResultChartsComponent.MEDIA_TYPE_BREAKDOWN_CHART_ID:
                this.mediaTypeBreakdownComponent.exportData(key);
                break;

            case MonitoringResultChartsComponent.COVERAGE_SUMMARY_CHART_ID:
                this.coverageSummaryChartName = this.classReference.COVERAGE_SUMMARY_CHART_ID + '_' + moment().format('YYYY-MM-DD');
                this.coverageSummaryComponent.exportData(key);
                break;

            default:
                break;
        }
    }

    private handleDateFacets(): void {
        if (!this.facetCollection || !this.facetCollection.dateFacets) {
            this.coverageOverTimeData.next(null);
            return;
        }

        const dateFacetValues = this.facetCollection.dateFacets.get(DateFacetTypesEnum.created);

        if (!dateFacetValues) {
            this.coverageOverTimeData.next(new DateTimeChartDataModel(null, null));
            return;
        }

        const coverageOverTimeDataItems: DateTimeChartDataItemModel[] = [];
        let scale: timeScalesEnum = null;

        if (dateFacetValues.length) {
            // Determine the time scale based on what was returned
            const timeDiff = moment.duration(
                moment(dateFacetValues[0].to).diff(moment(dateFacetValues[0].from))
            ).asSeconds();
            if (timeDiff > 3600) {
                scale = timeScalesEnum.daily;
            } else {
                scale = timeScalesEnum.hourly;
            }
        }

        dateFacetValues.forEach(
            dateEntry => {
                // Since amcharts only work with current time, we need to convert the time to user preference timezone (timestamp is changed)
                const dataItem = new DateTimeChartDataItemModel(
                    moment(moment(dateEntry.from).tz(this.userPreferenceTimezone).format('YYYY-MM-DD HH:mm:ss')).toDate(),
                    dateEntry.count,
                    {
                        from: moment(moment(dateEntry.from).tz(this.userPreferenceTimezone).format('YYYY-MM-DD HH:mm:ss')).toDate(),
                        to: moment(moment(dateEntry.to).tz(this.userPreferenceTimezone).format('YYYY-MM-DD HH:mm:ss')).toDate(),
                        tooltip: moment(dateEntry.from).tz(this.userPreferenceTimezone).format('MMM D'),
                    }
                );
                coverageOverTimeDataItems.push(dataItem);
            }
        );

        const coverageOverTimeData = new DateTimeChartDataModel(scale, coverageOverTimeDataItems);
        this.coverageOverTimeData.next(coverageOverTimeData);
    }

    private handleFacets(): void {
        if (this.facetCollection.facets === null) {
            this.mediaTypeBreakdownData.next(null);
            this.sentimentBreakdownData.next(null);
            return;
        }

        const mediaTypeFacets = this.facetCollection.facets.get(MentionSearchFacetTypesEnum.mediaType);
        const hasMediaTypeFacets = Array.isArray(mediaTypeFacets);

        if (hasMediaTypeFacets) {
            this.buildMediaTypeBreakdownData(mediaTypeFacets);
        }

        const sentimentFacets = this.facetCollection.facets.get(MentionSearchFacetTypesEnum.sentiment);
        const hasSentimentFacets = Array.isArray(sentimentFacets);

        if (hasSentimentFacets) {
            this.buildSentimentBreakdownData(sentimentFacets);
        }
    }

    private buildMediaTypeBreakdownData(mediaTypeFacets: FacetValueModel[]): void {
        const mediaTypeBreakdownDataMap = new Map<MediaTypeEnum, PieChartDataModel>();

        mediaTypeFacets.forEach(
            countEntry => {
                const mediaType = countEntry.value as MediaTypeEnum;

                if (this.mediaTypeColourMap.has(mediaType)) {
                    const pieChartData = new PieChartDataModel();

                    pieChartData.label = this.localizationService.transform(`MediaType.${mediaType}`);
                    pieChartData.value = countEntry.count;
                    pieChartData.color = this.mediaTypeColourMap.get(mediaType) || null;
                    pieChartData.legendValue = mediaType;

                    mediaTypeBreakdownDataMap.set(mediaType, pieChartData);
                }
            }
        );

        const mediaTypeBreakdownData: PieChartDataModel[] = [];

        // This is so that they always appear in the order specified by the MEDIA_TYPE_MAP
        for (const [key, value] of Array.from(this.mediaTypeColourMap)) {
            if (mediaTypeBreakdownDataMap.has(key)) {
                mediaTypeBreakdownData.push(mediaTypeBreakdownDataMap.get(key));
            }
        }

        this.mediaTypeBreakdownData.next(mediaTypeBreakdownData);
    }

    private buildSentimentBreakdownData(sentimentFacets: FacetValueModel[]): void {
        const negativePieChartData = new PieChartDataModel();
        negativePieChartData.label = this.localizationService.transform('Reports.sentiment.negative');
        negativePieChartData.value = 0;
        negativePieChartData.color = this.sentimentColors.negative;
        negativePieChartData.legendValue = SearchCriteriaQuerySentimentsEnum.negative;

        const positivePieChartData = new PieChartDataModel();
        positivePieChartData.label = this.localizationService.transform('Reports.sentiment.positive');
        positivePieChartData.value = 0;
        positivePieChartData.color = this.sentimentColors.positive;
        positivePieChartData.legendValue = SearchCriteriaQuerySentimentsEnum.positive;

        const neutralPieChartData = new PieChartDataModel();
        neutralPieChartData.label = this.localizationService.transform('Reports.sentiment.neutral');
        neutralPieChartData.value = 0;
        neutralPieChartData.color = this.sentimentColors.neutral;
        neutralPieChartData.legendValue = SearchCriteriaQuerySentimentsEnum.neutral;

        const notAvailablePieChartData = new PieChartDataModel();
        notAvailablePieChartData.label = this.localizationService.transform('Reports.sentiment.notAvailable');
        notAvailablePieChartData.value = 0;
        notAvailablePieChartData.color = this.sentimentColors.notAvailable;
        notAvailablePieChartData.legendValue = SearchCriteriaQuerySentimentsEnum.notAvailable;

        sentimentFacets.forEach(
            countEntry => {
                const floatValue = parseFloat(countEntry.value);

                if (isNaN(floatValue)) {
                    notAvailablePieChartData.value += countEntry.count;

                    return;
                }

                // Negative -1 to -0.3
                // Positive 0.3 to 1
                // Neutral -0.2 to 0.2
                if (floatValue <= -0.3) {
                    negativePieChartData.value += countEntry.count;
                } else if (floatValue >= 0.3) {
                    positivePieChartData.value += countEntry.count;
                } else {
                    neutralPieChartData.value += countEntry.count;
                }
            }
        );

        const sentimentBreakdownData: PieChartDataModel[] = [];

        // Order matters here, this is the order they'll be displayed in the legend
        sentimentBreakdownData.push(positivePieChartData);
        sentimentBreakdownData.push(neutralPieChartData);
        sentimentBreakdownData.push(negativePieChartData);
        sentimentBreakdownData.push(notAvailablePieChartData);

        this.sentimentBreakdownData.next(sentimentBreakdownData);
    }

    /**
     * Modifies the date filter and notifies the filter service of the change
     */
    public dateQueryChanged(dateRange: DateQueryModel): void {
        this.mentionSearchService.setDateQuery(dateRange);
        this.mentionSearchService.search(true, true);
    }

    /**
     * Converts a mouse click on the chart to a DateQuery object and emits it
     */
    public coverageOverTimeClicked(dateRange: DateRange): void {
        if (!dateRange) {

            return;
        }

        const dateQuery: DateQueryModel = new DateQueryModel();
        dateQuery.type = DateQueryTypeEnum.ABSOLUTE;
        dateQuery.absoluteRange = {
            start:  moment(dateRange?.from).toDate(),
            end: moment(dateRange?.to).toDate()
        };

        this.dateQueryChanged(dateQuery);
    }

    ngOnDestroy() {
        this.subscriptions.forEach(subscription => subscription.unsubscribe());
    }
}
